import React, { useRef } from "react";
import { Paperclip, X, FileText, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { DOC_ACCEPT, DOC_MAX_BYTES, fmtSize } from "@shared/consultation/ConsultationDocuments";

export const MAX_BOOKING_FILES = 3;

// Optional supporting documents at booking (≤3 files, ≤10 MB each). Uploaded after the
// consultation is created, before payment.
export function BookingDocumentsPicker({ files, onChange }) {
  const inputRef = useRef(null);
  const add = (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    const next = [...files];
    for (const f of picked) {
      if (next.length >= MAX_BOOKING_FILES) { toast.error(`You can attach up to ${MAX_BOOKING_FILES} documents`); break; }
      if (f.size > DOC_MAX_BYTES) { toast.error(`${f.name} exceeds 10 MB`); continue; }
      if (next.some((x) => x.name === f.name && x.size === f.size)) continue;
      next.push(f);
    }
    onChange(next);
  };
  return (
    <div className="mt-4" data-testid="booking-documents">
      <p className="text-xs font-bold text-[#0A2540]">Supporting documents <span className="font-normal text-slate-400">(optional)</span></p>
      <p className="text-[11px] text-slate-500 mb-2">Notices, agreements, photos — PDF, JPG, PNG, WEBP, DOC, DOCX · up to {MAX_BOOKING_FILES} files · 10 MB each. The lawyer can read them before the consultation starts.</p>
      <input ref={inputRef} type="file" multiple accept={DOC_ACCEPT} className="hidden" onChange={add} data-testid="booking-documents-input" />
      {files.length > 0 && (
        <ul className="space-y-1.5 mb-2">
          {files.map((f) => (
            <li key={`${f.name}-${f.size}`} className="flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-xs" data-testid="booking-document-item">
              {f.type.startsWith("image/") ? <ImageIcon size={14} className="text-[#0A2540]" /> : <FileText size={14} className="text-[#0A2540]" />}
              <span className="flex-1 truncate font-semibold" title={f.name}>{f.name}</span>
              <span className="text-slate-400">{fmtSize(f.size)}</span>
              <button type="button" aria-label={`Remove ${f.name}`} onClick={() => onChange(files.filter((x) => x !== f))} className="text-slate-400 hover:text-rose-600" data-testid="booking-document-remove"><X size={14} /></button>
            </li>
          ))}
        </ul>
      )}
      <button type="button" onClick={() => inputRef.current?.click()} disabled={files.length >= MAX_BOOKING_FILES} data-testid="booking-documents-add"
        className="inline-flex items-center gap-1 text-xs font-bold text-[#0A2540] border border-dashed border-slate-300 rounded-xl px-3 py-2 hover:bg-slate-50 disabled:opacity-40">
        <Paperclip size={14} /> {files.length ? "Add another document" : "Attach documents"} ({files.length}/{MAX_BOOKING_FILES})
      </button>
    </div>
  );
}
